import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

// 1. Import the local config (which contains the staging credentials)
import stagingConfig from '../firebase-applet-config.json';

// 8. Determine the configuration to use
// In production (Vite), environment variables prefixed with VITE_ are available via import.meta.env
const isDev = import.meta.env.DEV;

// Detect if we are in a staging environment based on hostname or explicit env var
const isStaging = 
  import.meta.env.VITE_FIREBASE_ENV === 'staging' || 
  (typeof window !== 'undefined' && (
    // Common staging/preview patterns
    window.location.hostname.includes('staging') || 
    window.location.hostname.includes('ais-dev') || 
    window.location.hostname.includes('ais-pre') ||
    window.location.hostname.includes('preview') ||
    window.location.hostname.includes('test') ||
    window.location.hostname.includes('dev-') ||
    window.location.hostname.includes('beta') ||
    window.location.hostname.includes('sandbox') ||
    // AI Studio / Cloud Run preview URLs
    window.location.hostname.endsWith('.run.app') ||
    window.location.hostname.endsWith('.aistudio-solutions.com') ||
    // If we are NOT on the production domain, treat as staging
    !(
      window.location.hostname === 'agency-assessment-production.web.app' || 
      window.location.hostname === 'agency-assessment-production.firebaseapp.com' ||
      window.location.hostname === 'agency-assessment-production.com' ||
      window.location.hostname === 'www.agency-assessment-production.com' ||
      window.location.hostname === 'agency.insourcingway.com'
    )
  ));

const useStaging = isDev || isStaging;
const usingEnvVars = !!import.meta.env.VITE_FIREBASE_API_KEY;

// If we are in staging/dev mode, we prioritize the local staging config file.
// In production mode (built app), we prioritize environment variables.
const firebaseConfig = useStaging ? {
  apiKey: stagingConfig.apiKey,
  authDomain: stagingConfig.authDomain,
  projectId: stagingConfig.projectId,
  storageBucket: stagingConfig.storageBucket,
  messagingSenderId: stagingConfig.messagingSenderId,
  appId: stagingConfig.appId,
  measurementId: stagingConfig.measurementId || '',
  firestoreDatabaseId: stagingConfig.firestoreDatabaseId,
} : {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || stagingConfig.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || stagingConfig.authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || stagingConfig.projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || (stagingConfig as any).storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || (stagingConfig as any).messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || stagingConfig.appId,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || (stagingConfig as any).measurementId,
  firestoreDatabaseId: import.meta.env.VITE_FIREBASE_DATABASE_ID || (stagingConfig as any).firestoreDatabaseId,
};

if (import.meta.env.DEV) {
  console.log(`Firebase config source: ${useStaging ? 'STAGING CONFIG FILE' : (usingEnvVars ? 'ENVIRONMENT VARIABLES' : 'STAGING CONFIG FILE FALLBACK')}`);
  console.log(`Firebase Project ID: ${firebaseConfig.projectId}`);
  console.log(`Environment: ${isDev ? 'Development' : (isStaging ? 'Staging' : 'Production')}`);
}

// 3. Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// 4. Setup Firestore
const databaseId = firebaseConfig.firestoreDatabaseId;
export const db = databaseId && databaseId !== '(default)' 
  ? getFirestore(app, databaseId) 
  : getFirestore(app);

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}
